import { setSentryUser } from "@/helpers/sentry"
import { getTgUser, getTgWebApp } from "@/helpers/tgWebApp"
import { TAGS, api } from "@/store/api.ts"
import { setLanguage, setVolume } from "@/store/slices/app"
import { setSessionId } from "@/store/slices/gameplay"
import { setIsPlayerLoaded } from "@/store/slices/player"
import {
  IPlayerProfileInfoResponse,
  IPlayerProfileResponse,
  IPlayerProfileSettingsResponse,
  IPlayerProfileSettingsUpdateBody,
  IPlayerProfileStatisticsResponse
} from "."

const basePath = "/player"

export const playerApi = api.enhanceEndpoints({
  addTagTypes: [TAGS.PLAYER]
})

export const playerEndpoints = playerApi.injectEndpoints({
  endpoints: (builder) => ({
    getPlayerProfile: builder.query<IPlayerProfileResponse, void>({
      query: () => ({
        url: `${basePath}/profile`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.PLAYER, id: "PROFILE" }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const data = (await queryFulfilled).data

          setSentryUser(data._id, undefined, {
            telegram_id: getTgUser()?.id?.toString(),
            telegram_username: getTgUser()?.username,
            language: getTgUser()?.language_code,
            language_version: getTgWebApp()?.version,
            platform: getTgWebApp()?.platform,
            session_id: data?.currentSessionId ?? ""
          })

          const currentSessionId = data.currentSessionId

          dispatch?.(setIsPlayerLoaded(true))
          currentSessionId && dispatch?.(setSessionId(currentSessionId))
        } catch {
          //catch
        }
      }
    }),
    getPlayerSettings: builder.query<IPlayerProfileSettingsResponse, void>({
      query: () => ({
        url: `${basePath}/personal-settings`,
        method: "GET"
      }),
      providesTags: () => [{ type: TAGS.PLAYER, id: "PROFILE_SETTINGS" }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const data = (await queryFulfilled).data

          dispatch(setLanguage(data.language))
          dispatch(setVolume(data.musicVolume ?? 0))
        } catch {
          //catch
        }
      }
    }),
    updatePlayerSettings: builder.mutation<
      IPlayerProfileSettingsResponse,
      IPlayerProfileSettingsUpdateBody
    >({
      query: (data) => ({
        url: `${basePath}/personal-settings`,
        method: "PATCH",
        data
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const updatedData = (await queryFulfilled).data

          dispatch(
            playerEndpoints.util.updateQueryData(
              "getPlayerSettings",
              undefined,
              () => updatedData
            )
          )
        } catch {
          //catch
        }
      }
    }),
    getPlayerInfo: builder.query<IPlayerProfileInfoResponse, string | void>({
      query: (playerId) => {
        let url: string

        if (playerId) {
          url = `${basePath}/personal-info/${playerId}`
        } else {
          url = `${basePath}/personal-info`
        }

        return {
          url,
          method: "GET"
        }
      },
      providesTags: (_, __, playerId) => [
        { type: TAGS.PLAYER, id: "PROFILE_INFO" },
        { type: TAGS.PLAYER, id: `PROFILE_INFO:${playerId}` }
      ]
    }),
    getPlayerStatistics: builder.query<
      IPlayerProfileStatisticsResponse,
      string | void
    >({
      query: (playerId) => {
        let url: string

        if (playerId) {
          url = `${basePath}/personal-statistics/short/${playerId}`
        } else {
          url = `${basePath}/personal-statistics`
        }

        return {
          url,
          method: "GET"
        }
      },
      providesTags: (_, __, playerId) => [
        { type: TAGS.PLAYER, id: "PROFILE_STATISTICS" },
        { type: TAGS.PLAYER, id: `PROFILE_STATISTICS:${playerId}` }
      ]
    })
  })
})

export const {
  useGetPlayerProfileQuery,
  useGetPlayerSettingsQuery,
  useGetPlayerInfoQuery,
  useGetPlayerStatisticsQuery,
  useUpdatePlayerSettingsMutation,
  endpoints: {
    getPlayerProfile,
    getPlayerSettings,
    getPlayerInfo,
    getPlayerStatistics
  }
} = playerEndpoints
